import React, { useLayoutEffect, useState } from 'react';

import { Button, Stack, Text } from '@mantine/core';
import { useElementSize } from '@mantine/hooks';

interface PackageDescriptionProps {
  description: string;
}

/** The package description, clamped to two lines, with a "Show more" toggle only when the text overflows them. */
const PackageDescription = ({ description }: PackageDescriptionProps): JSX.Element => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isClamped, setIsClamped] = useState(false);
  const { ref, width } = useElementSize<HTMLDivElement>();

  useLayoutEffect(() => {
    if (isExpanded || !ref.current) return;
    setIsClamped(ref.current.scrollHeight > ref.current.clientHeight);
  }, [description, width, isExpanded, ref]);

  return (
    <Stack spacing={0}>
      <Text
        ref={ref}
        size='sm'
        color='dark.3'
        lineClamp={isExpanded ? undefined : 2}
        data-cy='orderSummaryDescription'
      >
        {description}
      </Text>
      {isClamped && (
        <Button
          variant='subtle'
          compact
          size='xs'
          px={0}
          sx={{ alignSelf: 'flex-start' }}
          aria-expanded={isExpanded}
          onClick={() => setIsExpanded(expanded => !expanded)}
          data-cy='orderSummaryDescriptionToggle'
        >
          {isExpanded ? 'Show less' : 'Show more'}
        </Button>
      )}
    </Stack>
  );
};

export default PackageDescription;
